/**
 * Rewrites a Supabase Storage *public object* URL to the on-the-fly image
 * transform endpoint, returning a resized/recompressed variant.
 *
 * Why: menu photos are stored at full size (originals measured in prod: 1–2 MB
 * each). Customer-facing surfaces (kiosk, customer display, online ordering)
 * render them in small cards, and on the native tablet they bypass next/image
 * and are served at full size — the dominant cause of slow photo loading.
 * Serving a ~600px variant cuts each photo ~12× (≈1.9 MB → ≈150 KB).
 *
 * Supabase image transforms are a Pro-plan feature (verified active on this
 * project: /storage/v1/render/image returns 200).
 *
 * Safe by default: ONLY Supabase Storage public object URLs are rewritten.
 * `data:` URLs, local `/public` assets, other CDNs, and already-transformed
 * `render/image` URLs are returned unchanged.
 */
export function transformSupabaseImage(url: string, opts: { width: number; quality?: number }): string
export function transformSupabaseImage(url: null | undefined, opts: { width: number; quality?: number }): null
export function transformSupabaseImage(url: string | null | undefined, opts: { width: number; quality?: number }): string | null
export function transformSupabaseImage(
  url: string | null | undefined,
  opts: { width: number; quality?: number },
): string | null {
  if (!url) return null
  const OBJECT = '/storage/v1/object/public/'
  const idx = url.indexOf(OBJECT)
  if (idx === -1) return url // not a Supabase public object URL — leave untouched
  if (url.includes('/storage/v1/render/image/')) return url // already transformed

  const origin = url.slice(0, idx)
  const [objectPath, existingQuery] = url.slice(idx + OBJECT.length).split('?')
  const params = new URLSearchParams(existingQuery) // preserve any ?v= cache-buster
  params.set('width', String(opts.width))
  params.set('quality', String(opts.quality ?? 65))
  // Preserve aspect ratio. Supabase's render/image DISTORTS when given width
  // alone — it keeps the source's original height (a 2048×2048 source comes
  // back 600×2048), which reads as a hard zoom-in once object-fit:cover crops
  // it. `resize=contain` scales to the width keeping the source ratio; each
  // card's CSS object-fit then frames it exactly as before this transform.
  params.set('resize', 'contain')

  return `${origin}/storage/v1/render/image/public/${objectPath}?${params.toString()}`
}

/**
 * Menu/product photo sized for the customer-facing cards that actually display
 * it. Default 600px @ q65 ≈ 150 KB, sharp on retina tablets. Pass a larger
 * width for surfaces that show the photo bigger (hero/modal ≈ 900).
 */
export function menuPhotoUrl(url: string, width?: number): string
export function menuPhotoUrl(url: null | undefined, width?: number): null
export function menuPhotoUrl(url: string | null | undefined, width?: number): string | null
export function menuPhotoUrl(url: string | null | undefined, width = 600): string | null {
  return transformSupabaseImage(url, { width, quality: 65 })
}
